import _ from 'lodash';
import { DiagnosisResponse, ChartData, ChartSeries } from '../../types';

/**
 * Transforms diagnosis statistics into appointment counts chart data
 * @param diagnosisData The raw diagnosis response data
 * @returns Formatted data for appointments chart
 */
export const transformAppointmentsData = (diagnosisData: DiagnosisResponse): ChartData => {
  if (!diagnosisData?.stats) {
    return {
      series: [],
      categories: []
    };
  }

  // Sort diagnoses by name
  const sortedDiagnoses = _.orderBy(
    Object.entries(diagnosisData.stats),
    ([_, stats]) => stats.name.toLowerCase(),
    ['asc']
  );

  return {
    series: [{
      name: 'Total Appointments',
      data: sortedDiagnoses.map(([_, stats]) => stats.amount)
    }],
    categories: sortedDiagnoses.map(([_, stats]) => stats.name)
  };
};

/**
 * Transforms diagnosis statistics into unique patients chart data
 * @param diagnosisData The raw diagnosis response data
 * @returns Formatted data for unique patients chart
 */
export const transformUniquePatientData = (diagnosisData: DiagnosisResponse): ChartData => {
  if (!diagnosisData?.stats) {
    return {
      series: [],
      categories: []
    };
  }

  // Sort diagnoses by name
  const sortedDiagnoses = _.orderBy(
    Object.entries(diagnosisData.stats),
    ([_, stats]) => stats.name.toLowerCase(),
    ['asc']
  );

  // Everything this function emits is addressed one way: index i is diagnosis i.
  // `totalCounts` used to be filled by looking its slot up with categories.indexOf(name),
  // which returns the FIRST match -- so two codes sharing a name left the later row's
  // total at zero beside its own non-zero Female/Male. Positional throughout removes that,
  // and the `!== -1` guard it needed, since categories is built from this same list
  // (SHER-880).
  const categories = sortedDiagnoses.map(([, stats]) => stats.name);
  const totalCounts: number[] = sortedDiagnoses.map(([, stats]) => stats.patientsLength);
  const patientIdsByCategory: number[][] = sortedDiagnoses.map(([, stats]) => stats.patientIds);

  // Female/Male are placeholders here; DiagnosisTable computes them from the patient ids
  // once the decrypted patients are in the store.
  const femaleCounts: number[] = new Array(categories.length).fill(0);
  const maleCounts: number[] = new Array(categories.length).fill(0);

  return {
    series: [
      {
        name: 'Female',
        data: femaleCounts
      },
      {
        name: 'Male',
        data: maleCounts
      },
      {
        name: 'Total',
        data: totalCounts
      }
    ],
    categories,
    patientIdsByCategory
  };
};

